import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import { API_COMPANY_LOGO_UPLOAD, API_GET_COMPANY_LOGO } from "../../../Util/URLConstants";
import StorageFactory from "../../../Util/StorageFactory";
import { s3_vm_box_logo, SESSION_USER_ID_KEY, SESSION_USER_TYPE_KEY } from "../../../Util/VMConstants";
import { isAdminLoggedIn } from "../../../Util/UtilHelper";
import SubscriptionDialog from "../../../Util/SubscriptionDialog";

const VENUE_MANAGER_LOGO_URL = s3_vm_box_logo; 

const CompanyLogoUploader = ({ enquiry_id }) => {
  const [logo, setLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
  const fileInputRef = useRef(null);
  const subscriptionStatus = useSelector(
    (state) => state.subscription.status
  );
  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  const isAdmin = isAdminLoggedIn(userType);

  useEffect(() => {
    if (userID) fetchLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, enquiry_id]);

  const fetchLogo = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(API_GET_COMPANY_LOGO, {
        params: { user_id: userID, enquiry_id },
      });
      if (res?.data?.status_code === 200) {
        const apiLogo = res.data.data?.company_logo;
        setLogo(apiLogo && apiLogo.trim() !== "" ? apiLogo : VENUE_MANAGER_LOGO_URL);
      } else {
        setLogo(VENUE_MANAGER_LOGO_URL);
        console.warn("Unexpected response fetching company logo:", res?.data);
      }
    } catch (err) {
      console.error("Failed to fetch logo:", err?.message || err);
      setLogo(VENUE_MANAGER_LOGO_URL);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (!isAdmin) return;
    if (subscriptionStatus?.plan === "premium") {
      setOpenSubscribeDialog(false);
      fileInputRef.current?.click();
    } else {
      setOpenSubscribeDialog(true);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setLogo(previewUrl);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("enquiry_id", enquiry_id);
      formData.append("user_id", userID);

      const res = await axios.post(API_COMPANY_LOGO_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.data?.status_code !== 200) {
        throw new Error(res?.data?.message || "Upload failed");
      }

      // refresh final url from API
      await fetchLogo();
    } catch (err) {
      console.error("Logo upload failed:", err?.message || err);
      alert("Logo upload failed: " + (err?.message || "Unknown error"));
      await fetchLogo();
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        width: "100%",
        borderRadius: 2,
        overflow: "visible",
      }}
    >
      <CardContent sx={{ display: "flex", gap: 3, alignItems: "center", p: 3 }}>
        <Box
          sx={{
            position: "relative",
            width: 120,
            height: 120,
            borderRadius: 2,
            boxShadow: "0 6px 18px rgba(16,24,40,0.08)",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
          }}
        >
          <Avatar
            src={logo || VENUE_MANAGER_LOGO_URL}
            alt="Company Logo"
            variant="rounded"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 2,
              objectFit: "contain",
            }}
          />

          {/* Loading indicator for all users; Change overlay — admin only */}
          {(isLoading || uploading) && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.6)",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
          {isAdmin && !isLoading && !uploading && (
            <Box
              onClick={handleUploadClick}
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.36) 100%)",
                color: "#fff",
                cursor: "pointer",
                transition: "opacity 180ms ease",
                "&:hover": { opacity: 0.95 },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 2,
                  py: 0.5,
                  backgroundColor: "rgba(0,0,0,0.45)",
                  borderRadius: 999,
                }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Change
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Company Logo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            { "Upload a high-resolution transparent logo (recommended PNG or JPEG). This will appear on proposals and invoices."
            }
          </Typography>

          {isAdmin && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUploadClick}
                disabled={uploading || isLoading}
                sx={{
                  px: 2.5,
                  py: 1,
                }}
              >
                {uploading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Upload / Change"
                )}
              </Button>
            </Stack>
          )}
        </Box>
      </CardContent>

      {isAdmin && (
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      )}

      <SubscriptionDialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        onSubscribe={() => {
          setOpenSubscribeDialog(false);
          window.location.href = "/subscription";
        }}
      />
    </Card>
  );
};

export default CompanyLogoUploader;
